import * as LucideIcons from "lucide-react";
import { motion } from "framer-motion";
import ConfidenceBadge from "@/components/ConfidenceBadge";

function scoreColor(score) {
  // Phase 7 (v1 launch cleanup): an Unavailable category (score is null —
  // no measurable evidence this run) must never fall through to red. Red
  // is reserved for a genuinely measured weakness. Reuse the same amber
  // the app already uses for "Unavailable" everywhere else (see
  // ConfidenceBadge.jsx) rather than inventing a new color.
  if (score == null) return "#EAB308";
  if (score >= 75) return "#6CC24A";
  if (score >= 50) return "#EAB308";
  return "#EF4444";
}

export default function CategoryCard({ category, index = 0, onOpen }) {
  const Icon = LucideIcons[category.icon] || LucideIcons.Shield;
  const color = scoreColor(category.score);
  const isModel = (category.confidence || "calculated") !== "verified";
  // Reuses the backend's existing roadmap_only evidence flag — a
  // category has "partial scope" when at least one of its signals is
  // structurally unmeasurable under the current build, not merely
  // unresolved this run. No new evidence status, no scoring change.
  const partialScope = (category.evidence || []).some((e) => e.roadmap_only);
  return (
    <motion.button
      type="button"
      onClick={onOpen}
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: 0.05 * index, ease: "easeOut" }}
      className="pp-card text-left p-5 hover:bg-[#262d36] transition-colors group relative overflow-hidden"
      data-testid={`category-card-${category.id}`}
      data-model={isModel ? "true" : "false"}
    >
      {isModel && (
        <div
          className="absolute top-0 left-0 right-0 h-0.5"
          style={{ background: "#EAB308" }}
          aria-hidden
        />
      )}
      <div className="flex items-start justify-between">
        <div className="p-2 border border-[#333C47] bg-[#1A1F24]">
          <Icon className="h-4 w-4" style={{ color }} />
        </div>
        <div
          className="metric text-2xl font-bold"
          style={{ color: "#FFFFFF" }}
          data-testid={`category-score-${category.id}`}
        >
          {category.score != null ? category.score : "—"}
          <span className="text-sm text-[#C7CBD1]">/100</span>
        </div>
      </div>
      <div className="mt-4">
        <div className="text-white font-display font-semibold text-base leading-tight">
          {category.name}
        </div>
        <div className="mt-1.5 text-xs text-[#C7CBD1] leading-relaxed line-clamp-2">
          {category.summary}
        </div>
      </div>
      <div className="mt-4">
        <div className="h-1.5 bg-[#1A1F24] border border-[#333C47] overflow-hidden">
          <motion.div
            initial={{ width: 0 }}
            animate={{ width: `${category.score ?? 0}%` }}
            transition={{ duration: 1.4, delay: 0.05 * index, ease: [0.22, 0.61, 0.36, 1] }}
            className="h-full"
            style={{ background: color }}
          />
        </div>
      </div>
      <div className="mt-4 flex items-center justify-between gap-2">
        <ConfidenceBadge
          confidence={category.confidence || "calculated"}
          source={category.source}
          partialScope={partialScope}
        />
        {typeof category.confidence_pct === "number" && (
          <span
            className="text-[10px] uppercase tracking-wider text-[#C7CBD1]"
            data-testid={`category-confidence-pct-inline-${category.id}`}
          >
            {category.confidence_pct}% confidence
          </span>
        )}
      </div>
      <div className="mt-3 text-xs text-[#6CC24A] text-right group-hover:underline">
        Improve →
      </div>
    </motion.button>
  );
}
